import type { BrandTheme } from '../types/gotypes'
import { refuse, type WhoToAsk } from './permissionReason'

/**
 * Three-tier ownership for brand themes (#1266), the DataViz model exactly.
 * Mirrors the server's canEditBrandTheme (goapi/pkg/server/brand_themes.go):
 * the owner always; a Manager or Realm user only once the theme is
 * company-level (promoted). A colleague's private theme is nobody's to edit
 * but theirs — Duplicate it. The server enforces the same rule on its own.
 */
export function canEditBrandTheme(theme: BrandTheme | null | undefined, userEmail: string, userCanPromote: boolean): boolean {
  if (!theme) return false
  if (theme.user && theme.user === userEmail) return true
  return Boolean(theme.promoted_at) && userCanPromote
}

/**
 * The person to ask about a theme the viewer cannot change: a private
 * colleague's names its owner; a company-level theme has no owner to ask
 * (only a Manager can change it), so it names the viewer's team manager.
 */
const askOwnerOf = (theme: BrandTheme) => (theme.promoted_at ? undefined : theme.user || undefined)

/** Why a disabled edit control is disabled (#1262: disable and say why, never hide). */
export const noThemeEditReason = (who: WhoToAsk, theme: BrandTheme): string =>
  refuse("You can't edit this theme.", who, { owner: askOwnerOf(theme), copy: 'Duplicate' }).reason

/** Why a disabled delete control is disabled. A copy does not help, so none is offered. */
export const noThemeDeleteReason = (who: WhoToAsk, theme: BrandTheme): string =>
  refuse("You can't delete this theme.", who, { owner: askOwnerOf(theme) }).reason

export interface BrandThemeTiers {
  company: BrandTheme[]
  mine: BrandTheme[]
  colleagues: BrandTheme[]
}

/** Tiers are mutually exclusive: a promoted theme is company-level whoever owns it. */
export function groupBrandThemes(themes: BrandTheme[], userEmail: string): BrandThemeTiers {
  const tiers: BrandThemeTiers = { company: [], mine: [], colleagues: [] }
  for (const t of themes) {
    if (t.promoted_at) tiers.company.push(t)
    else if (t.user && t.user === userEmail) tiers.mine.push(t)
    else tiers.colleagues.push(t)
  }
  return tiers
}
